import { Link } from 'react-router-dom';
import { ShoppingCart, Heart, MessageCircle, GitCompare, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useCart } from '@/contexts/CartContext';
import { useWishlist } from '@/contexts/WishlistContext';
import { getProductWhatsAppUrl } from '@/lib/whatsapp';
import { motion } from 'framer-motion';
import type { DbProduct } from '@/hooks/useProducts';

interface ProductCardProps {
  product: DbProduct;
}

const ProductCard = ({ product }: ProductCardProps) => {
  if (!product) return null;
  
  const { addToCart } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist, addToCompare, isInCompare } = useWishlist();
  const liked = isInWishlist(product?.id ?? '');
  const compared = isInCompare(product?.id ?? '');

  const cartProduct = {
    id: product?.id ?? '',
    name: product?.name ?? '',
    brand: product?.brand ?? '',
    category: (product?.category as any) ?? 'smartphones',
    price: product?.price ?? 0,
    originalPrice: product?.original_price ?? 0,
    discount: product?.discount ?? 0,
    image: product?.image ?? '',
    images: product?.images ?? [],
    rating: product?.rating ?? 0,
    reviews: product?.reviews ?? 0,
    description: product?.description ?? '',
    specs: (product?.specs ?? {}) as Record<string, string>,
    inStock: product?.in_stock ?? true,
    isFlashSale: product?.is_flash_sale ?? false,
    isFeatured: product?.is_featured ?? false,
  };

  const wishlistProduct = cartProduct;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="group bg-card rounded-xl border shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden"
    >
      <div className="relative overflow-hidden bg-muted">
        <Link to={`/product/${product.id}`}>
          <div className="w-full h-48 relative bg-white">
            <img
              src={product?.image ?? '/placeholder.svg'}
              alt={product?.name ?? 'Product'}
              className="w-full h-full object-contain p-2"
              loading="lazy"
              onError={(e) => {
                const target = e.currentTarget;
                target.src = '/placeholder.svg';
                target.onerror = null;
              }}
            />
          </div>
        </Link>

        {(product?.discount ?? 0) > 0 && (
          <Badge className="absolute top-2 left-2 bg-destructive text-destructive-foreground text-xs font-bold">
            -{product.discount}%
          </Badge>
        )}

        {product?.is_flash_sale && (
          <Badge className="absolute top-2 right-2 bg-warning text-primary-foreground text-xs font-bold animate-pulse">
            ⚡ Flash Sale
          </Badge>
        )}

        <div className="absolute bottom-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="secondary"
            size="icon"
            className="h-8 w-8 rounded-full shadow-md"
            onClick={() => liked ? removeFromWishlist(product?.id ?? '') : addToWishlist(wishlistProduct)}
          >
            <Heart className={`h-4 w-4 ${liked ? 'fill-destructive text-destructive' : ''}`} />
          </Button>
          <Button
            variant="secondary"
            size="icon"
            className="h-8 w-8 rounded-full shadow-md"
            onClick={() => addToCompare(wishlistProduct)}
            disabled={compared}
          >
            <GitCompare className={`h-4 w-4 ${compared ? 'text-accent' : ''}`} />
          </Button>
        </div>
      </div>

      <div className="p-4 space-y-2">
        <p className="text-xs text-muted-foreground uppercase tracking-wider">{product?.brand ?? ''}</p>
        <Link to={`/product/${product?.id ?? ''}`}>
          <h3 className="font-heading font-semibold text-sm leading-tight hover:text-accent transition-colors line-clamp-2">
            {product?.name ?? 'Product'}
          </h3>
        </Link>

        <div className="flex items-center gap-1">
          <Star className="h-3.5 w-3.5 fill-gold text-gold" />
          <span className="text-xs font-medium">{product?.rating ?? 0}</span>
          <span className="text-xs text-muted-foreground">({(product?.reviews ?? 0).toLocaleString()})</span>
        </div>

        <div className="flex items-center gap-2">
          <span className="font-heading font-bold text-lg">₹{(product?.price ?? 0).toLocaleString('en-IN')}</span>
          {(product?.original_price ?? 0) > (product?.price ?? 0) && (
            <span className="text-sm text-muted-foreground line-through">₹{(product?.original_price ?? 0).toLocaleString('en-IN')}</span>
          )}
        </div>

        <div className="flex gap-2 pt-1">
          <Button size="sm" className="flex-1 text-xs h-8" onClick={() => addToCart(cartProduct)}>
            <ShoppingCart className="h-3 w-3 mr-1" /> Add
          </Button>
          <a href={getProductWhatsAppUrl(product?.name ?? 'Product', product?.price ?? 0)} target="_blank" rel="noopener noreferrer">
            <Button size="sm" variant="outline" className="h-8 text-xs border-success text-success hover:bg-success hover:text-primary-foreground">
              <MessageCircle className="h-3 w-3" />
            </Button>
          </a>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;
